import { QuestionCircleOutlined } from "@ant-design/icons";
import { Input, Select, Tooltip } from "antd";
import { getStreetItems, StreetResponse, updateStreetItems } from "../../store/Streets.repository";
import { useObservable } from "@ngneat/use-observable";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ENTRYPOINT } from "../../../map/Endpoints";
import { findError, NewFeatureListContext } from "../Map-marker-list-wrapper";
import { transform } from "ol/proj";
import { Point, SimpleGeometry } from "ol/geom";
import { MINIMAP } from "../Map-marker-mini-map";
import { MARKER_LAYER } from "../../Map-marker";
import axios from "axios";
import WKT from "ol/format/WKT";
import { getUid } from "ol";

export const MapMarkerLinearRefForm = () => {
	const [items] = useObservable(getStreetItems());
	const { selectedItem, tag, sticky, detour, onError, errors, removeError, onLoading } = useContext(NewFeatureListContext);
	const [placeholder, setPlaceholder] = useState<{ min?: string; max?: string }>({ min: selectedItem!.get("minPlaceholder"), max: selectedItem!.get("maxPlaceholder") });
	const [street, setStreet] = useState(selectedItem?.get("street"));
	const [start, setStart] = useState<string>((selectedItem?.get("start") ?? "").toString());
	const [end, setEnd] = useState<string>((selectedItem?.get("end") ?? "").toString());

	const skipNextFetch = useRef(false);

	const hasMounted = useRef(false);

	const error = findError(selectedItem, errors);

	useEffect(() => {
		if (items.length < 1) {
			axios.get<StreetResponse>(ENTRYPOINT + "/api/caps/microroadnetwork/?limit=99999").then((response) => {
				updateStreetItems(response.data.results, response.data.next);
			});
		}
	}, [items]);

	useEffect(() => {
		if (!selectedItem) return;

		const handleChange = () => {
			setStreet(selectedItem.get("street"));
			setStart((selectedItem.get("start") ?? "").toString());
			setEnd((selectedItem.get("end") ?? "").toString());
			skipNextFetch.current = true; // mark that next fetch should be skipped
		};

		selectedItem.on("change", handleChange);

		return () => {
			selectedItem.un("change", handleChange);
		};
	}, [selectedItem]);

	const zoomOnGeom = () => {
		MINIMAP.getView().fit(selectedItem!.getGeometry() as SimpleGeometry, { maxZoom: 12, duration: 500, padding: [16, 16, 16, 16] });
	};

	const checkIfLine = () => {
		if (detour) {
			return false;
		}

		const features = MARKER_LAYER.getSource()!
			.getFeatures()
			.filter((f) => !f.get("detour"));

		return features.length > 1 && features[0].getGeometry() instanceof Point;
	};

	const fetchData = useCallback(() => {
		const features = MARKER_LAYER.getSource()!
			.getFeatures()
			.filter((f) => !f.get("detour"));

		if (!street || !start) return;

		if ((detour || (features.length > 1 && features[0].getGeometry()?.getType() === "MultiLineString")) && !end) return;

		const data = {
			route_id: street,
			...(start && end
				? { from_measure: Number(start.replaceAll(",", ".")), to_measure: Number(end.replaceAll(",", ".")), tag, not_sticky: !sticky }
				: { measure: start.replaceAll(",", "."), tag, not_sticky: !sticky }),
		};

		onLoading(true);
		axios
			.post(ENTRYPOINT + "/lr/getlrandcoords", data)
			.then((response) => {
				onLoading(false);
				const res = response.data[0];
				if (start && end) {
					selectedItem!.setProperties(
						{
							addresses: res.addresses,
							start: res.from_measure,
							end: res.to_measure,
							street: res.route_id,
							openlr: res.openlr,
							detour: detour,
						},
						true
					);
					const geom = new WKT().readGeometry(res.geometry_wkt, {
						dataProjection: "EPSG:3059",
						featureProjection: "EPSG:3857",
					});
					selectedItem!.setGeometry(geom);
					if (end > res.to_measure) setEnd(res.to_measure?.toString());
				} else {
					selectedItem!.setProperties(
						{
							addresses: res.addresses,
							start: res.measure,
							street: res.route_id,
							openlr: res.openlr,
						},
						true
					);
					const coord = transform([res.point.x, res.point.y], "EPSG:3059", "EPSG:3857");
					selectedItem!.setGeometry(new Point(coord));
				}
				zoomOnGeom();
				removeError(selectedItem!);
			})
			.catch((e) => {
				console.error(e);
				onError({ ...e.response.data, uid: getUid(selectedItem) });
			});
	}, [start, end, street, detour]);

	useEffect(() => {
		if (!hasMounted.current) {
			hasMounted.current = true;
			return;
		}

		if (skipNextFetch.current) {
			skipNextFetch.current = false; // reset flag
			return;
		}

		const handler = setTimeout(fetchData, 1000);
		return () => clearTimeout(handler);
	}, [fetchData]);

	return (
		<div className="grid grid-cols-4 gap-2 mt-4 items-center">
			<p>
				Lineārā reference{" "}
				<Tooltip
					title="Ievadiet ceļa indeksu, sākuma un beigu km valsts autoceļu maršrutu tīklā.
Ceļu indeksā L - labās puses brauktuve, K - reisās puses brauktuve, I - viena brauktuve."
				>
					<QuestionCircleOutlined className="text-gray-500" />
				</Tooltip>{" "}
				:
			</p>
			<div className="grid-cols-2 gap-2 col-span-3 grid">
				<Select
					className="col-span-2"
					onChange={(e) => {
						setStreet(e.toString());
						selectedItem!.set("street", e.toString(), true);
						const value = items.find((entity) => entity.routeid === e);
						if (value) {
							const min = value.min_measure?.toString();
							const max = value.max_measure?.toString();
							selectedItem!.setProperties({ minPlaceholder: min, maxPlaceholder: max });
							setPlaceholder({ min, max });
						}
						skipNextFetch.current = false;
					}}
					value={street}
					showSearch
					filterSort={(a, b) =>
						a.label?.charAt(0).localeCompare(b.label.charAt(0)) === 0 ? a.label.length - b.label.length : a.label?.charAt(0).localeCompare(b.label?.charAt(0))
					}
					placeholder={"Ceļa indekss"}
					id={"address"}
					options={items.map((i) => ({ value: i.routeid, label: i.routeid }))}
				/>
				<Input
					className=""
					onChange={(el) => {
						setStart(el.target.value.toString());
						selectedItem!.setProperties({ start: el.target.value }, true);
						skipNextFetch.current = false;
					}}
					onInput={() => {
						onLoading(true);
					}}
					value={start}
					placeholder={placeholder.min ?? "Sākuma punkts"}
					id={"start"}
					status={error?.error === "measure_error" ? "error" : ""}
				/>
				<Input
					className=""
					onChange={(el) => {
						setEnd(el.target.value.toString());
						selectedItem!.setProperties({ end: el.target.value }, true);
						skipNextFetch.current = false;
					}}
					onInput={() => {
						onLoading(true);
					}}
					disabled={checkIfLine()}
					value={end}
					placeholder={placeholder.max ?? "Beigu punkts"}
					id={"end"}
					status={error?.error === "measure_error" ? "error" : ""}
				/>
				{error?.error === "measure_error" && <p className="col-span-2 text-[#ff4d4f] text-center leading-tight">{error.message}</p>}
			</div>
		</div>
	);
};
